import React from 'react';
import { Form } from '@/components/ui';

interface ServerPaginationProps {
  currentPage: number;
  totalPages: number;
  totalCount: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
  loading?: boolean;
  maxVisiblePages?: number;
  className?: string;
  pageSizeOptions?: number[];
}

const ServerPagination: React.FC<ServerPaginationProps> = ({
  currentPage,
  totalPages,
  totalCount,
  pageSize,
  onPageChange,
  onPageSizeChange,
  loading = false,
  maxVisiblePages = 5,
  className = '',
  pageSizeOptions: pageSizeOptionsProp
}) => {
  const pageSizeOptions = pageSizeOptionsProp ?? [20, 50, 100];

  if (totalPages <= 1 && totalCount <= pageSize) return null;

  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalCount);

  let pages = [];

  if (totalPages <= maxVisiblePages) {
    // Show all pages if there are fewer than maxVisiblePages
    pages = Array.from({ length: totalPages }, (_, i) => i + 1);
  } else {
    // Always include first and last page
    pages.push(1);
    
    // Calculate start and end of the middle section
    let startPage = Math.max(2, currentPage - Math.floor(maxVisiblePages / 2) + 1);
    let endPage = Math.min(totalPages - 1, startPage + maxVisiblePages - 3);
    
    // Adjust if we're near the end
    if (endPage === totalPages - 1) {
      startPage = Math.max(2, endPage - (maxVisiblePages - 3));
    }
    
    // Add ellipsis if needed
    if (startPage > 2) {
      pages.push(-1); // -1 represents ellipsis
    }
    
    // Add middle pages
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    
    // Add ellipsis if needed
    if (endPage < totalPages - 1) {
      pages.push(-2); // -2 represents ellipsis
    }
    
    // Add last page if not already included
    if (totalPages > 1) {
      pages.push(totalPages);
    }
  }

  return (
    <div className={`tw-flex tw-flex-col tw-gap-3 ${className}`}>
      {/* Results info and page size selector */}
      <div className="tw-flex tw-flex-col sm:tw-flex-row tw-justify-between tw-items-center tw-gap-3">
        <div className="tw-text-content-muted tw-text-sm">
          Showing {startItem} to {endItem} of {totalCount} results
        </div>

        <div className="tw-flex tw-items-center tw-gap-2">
          <label htmlFor="pageSize" className="tw-text-content-muted tw-text-sm tw-mb-0">
            Show:
          </label>
          <Form.Select
            id="pageSize"
            size="sm"
            style={{ width: 'auto', position: 'relative', zIndex: 1001 }}
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            disabled={loading}
          >
            {pageSizeOptions.map(size => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </Form.Select>
          <span className="tw-text-content-muted tw-text-sm">per page</span>
        </div>
      </div>

      {/* Centered pagination controls */}
      <div className="tw-flex tw-justify-center">
        <nav aria-label="Server Pagination">
          <ul className="tw-flex -tw-space-x-px tw-mb-0 tw-list-none tw-p-0 tw-text-sm">
            {/* First page button */}
            <li>
              <button
                className="tw-min-w-[2rem] tw-border tw-border-line tw-px-2 tw-py-1 tw-bg-surface tw-text-primary hover:tw-bg-surface-hover disabled:tw-opacity-50"
                onClick={() => onPageChange(1)}
                disabled={currentPage === 1 || loading}
                aria-label="First page"
              >
                &laquo;
              </button>
            </li>

            {/* Previous page button */}
            <li>
              <button
                className="tw-min-w-[2rem] tw-border tw-border-line tw-px-2 tw-py-1 tw-bg-surface tw-text-primary hover:tw-bg-surface-hover disabled:tw-opacity-50"
                onClick={() => onPageChange(currentPage - 1)}
                disabled={currentPage === 1 || loading}
                aria-label="Previous page"
              >
                &lsaquo;
              </button>
            </li>

            {/* Page numbers */}
            {pages.map((page, index) => (
              page < 0 ? (
                <li key={`ellipsis-${index}`}>
                  <span className="tw-inline-block tw-min-w-[2rem] tw-border tw-border-line tw-px-2 tw-py-1 tw-bg-surface tw-text-content-muted tw-text-center">...</span>
                </li>
              ) : (
                <li key={page}>
                  <button
                    className={`tw-min-w-[2rem] tw-border tw-border-line tw-px-2 tw-py-1 hover:tw-bg-surface-hover disabled:tw-opacity-50 ${currentPage === page ? 'tw-bg-primary tw-text-white tw-border-primary' : 'tw-bg-surface tw-text-primary'}`}
                    onClick={() => onPageChange(page)}
                    disabled={loading}
                  >
                    {page}
                  </button>
                </li>
              )
            ))}

            {/* Next page button */}
            <li>
              <button
                className="tw-min-w-[2rem] tw-border tw-border-line tw-px-2 tw-py-1 tw-bg-surface tw-text-primary hover:tw-bg-surface-hover disabled:tw-opacity-50"
                onClick={() => onPageChange(currentPage + 1)}
                disabled={currentPage === totalPages || loading}
                aria-label="Next page"
              >
                &rsaquo;
              </button>
            </li>

            {/* Last page button */}
            <li>
              <button
                className="tw-min-w-[2rem] tw-border tw-border-line tw-px-2 tw-py-1 tw-bg-surface tw-text-primary hover:tw-bg-surface-hover disabled:tw-opacity-50"
                onClick={() => onPageChange(totalPages)}
                disabled={currentPage === totalPages || loading}
                aria-label="Last page"
              >
                &raquo;
              </button>
            </li>
          </ul>
        </nav>
      </div>
    </div>
  );
};

export default ServerPagination; 